/**
 * DisclaimerPage.tsx
 *
 * First-login gate. Renders pending disclaimers (general first, then owner)
 * one at a time. The "I Agree" button activates only after the user has
 * scrolled to the bottom of the body AND ticked the acknowledgement box —
 * the standard pattern for legally meaningful click-through consent.
 *
 * The pending queue is sourced from the auth store, which is hydrated by the
 * login pages (and persisted) so a hard refresh cannot bypass the gate.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { disclaimersApi } from '@/services/api';
import { getErrorMessage } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';
import { redirectToCorpLogout } from '@/lib/corpAuth';
import { DatePicker } from '@/components/ui/DatePicker';
import { getLegacyOrigin } from '@/lib/appStage';

const PROSPERA_LOGO_URL = `${getLegacyOrigin()}/uimages/img8/brandlogo.png`;
const SCROLL_TOLERANCE_PX = 24;

export function DisclaimerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const pendingDisclaimers = useAuthStore((s) => s.pendingDisclaimers);
  const acknowledgeDisclaimer = useAuthStore((s) => s.acknowledgeDisclaimer);
  const setPendingDisclaimers = useAuthStore((s) => s.setPendingDisclaimers);
  const logout = useAuthStore((s) => s.logout);

  const current = pendingDisclaimers[0];

  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/';

  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [signedName, setSignedName] = useState('');
  const [signedBirthDate, setSignedBirthDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Reset per-disclaimer UI state when advancing to the next item. Each
  // disclaimer in the queue is signed independently with a fresh form.
  useEffect(() => {
    setScrolledToBottom(false);
    setAgreed(false);
    setSignedName('');
    setSignedBirthDate('');
    setError(null);

    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
      // If the body is already short enough that there's nothing to scroll,
      // count the user as having "reached the bottom" immediately.
      const el = bodyRef.current;
      if (el.scrollHeight - el.clientHeight <= SCROLL_TOLERANCE_PX) {
        setScrolledToBottom(true);
      }
    }
  }, [current?.slug, current?.code, current?.version]);

  // Defensive: if someone deep-links to /disclaimer with nothing pending,
  // bounce them to the destination. RequireAuth shouldn't allow this but
  // we want predictable behavior either way.
  useEffect(() => {
    if (pendingDisclaimers.length === 0) {
      navigate(from, { replace: true });
    }
  }, [pendingDisclaimers.length, navigate, from]);

  const sanitizedBody = useMemo(() => {
    if (!current) return '';
    return DOMPurify.sanitize(current.bodyHtml, { USE_PROFILES: { html: true } });
  }, [current]);

  // Capture the queue size on first render so "Step N of M" displays a monotonically
  // increasing N as items are acknowledged off the head. Above the `!current` guard
  // because it is a hook: acknowledging the last disclaimer empties the queue, and a
  // render that takes the guard would otherwise run one hook fewer than the render
  // before it (React #300).
  const totalStepsRef = useRef(pendingDisclaimers.length);
  if (pendingDisclaimers.length > totalStepsRef.current) {
    totalStepsRef.current = pendingDisclaimers.length;
  }

  if (!current) return null;

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_TOLERANCE_PX) {
      setScrolledToBottom(true);
    }
  }

  async function handleAgree() {
    if (!current || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await disclaimersApi.accept(
        current.slug,
        current.code,
        current.version,
        signedName.trim(),
        signedBirthDate,
      );
      acknowledgeDisclaimer(current.slug, current.code);
      // If the queue is now empty, RequireAuth's effect will redirect; we
      // also navigate explicitly so the transition is immediate.
      const remaining = useAuthStore.getState().pendingDisclaimers;
      if (remaining.length === 0) {
        navigate(from, { replace: true });
      }
    } catch (err: unknown) {
      // 422 mismatch means the version moved on under us — refetch the
      // queue so the user sees the latest text.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 422) {
        try {
          const fresh = await disclaimersApi.getPending();
          setPendingDisclaimers(fresh);
          if (fresh.length === 0) {
            navigate(from, { replace: true });
            return;
          }
          setError('The disclaimer was updated. Please review the latest version.');
        } catch (refetchErr) {
          setError(getErrorMessage(refetchErr, 'Could not load the latest disclaimer.'));
        }
      } else {
        setError(getErrorMessage(err, 'Could not record your agreement. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleDecline() {
    logout();
    // Declining is a deliberate logout → corp login, forcing the account list
    // (also avoids bouncing straight back into the same account's disclaimer).
    redirectToCorpLogout();
  }

  const totalSteps = totalStepsRef.current;
  const stepNumber = Math.max(1, totalSteps - pendingDisclaimers.length + 1);

  const acceptDisabled =
    !scrolledToBottom ||
    !agreed ||
    signedName.trim() === '' ||
    signedBirthDate === '' ||
    submitting;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#040816] px-4 py-10 text-white">
      <div className="flex w-full max-w-3xl flex-col">
        <div className="mb-6 flex justify-center">
          <img
            src={PROSPERA_LOGO_URL}
            alt="Prospera"
            className="h-auto w-full max-w-[220px] object-contain"
          />
        </div>

        <div className="flex max-h-[85vh] flex-col rounded-2xl border border-white/10 bg-[#141a2b] shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
          <div className="flex items-start justify-between gap-4 border-b border-white/10 px-6 py-5">
            <div className="min-w-0">
              <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-slate-400">
                Step {stepNumber} of {totalSteps}
              </p>
              <h1 className="mt-1 truncate text-xl font-semibold text-white">{current.title}</h1>
            </div>
            <span className="shrink-0 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-300">
              v{current.version}
            </span>
          </div>

          <div
            ref={bodyRef}
            onScroll={handleScroll}
            className="prose prose-sm prose-invert max-w-none flex-1 overflow-y-auto px-6 py-5 text-slate-200"
            dangerouslySetInnerHTML={{ __html: sanitizedBody }}
          />

          <div className="space-y-4 border-t border-white/10 px-6 py-5">
            {error && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {error}
              </div>
            )}

            {!scrolledToBottom && (
              <p className="text-xs text-slate-400">
                Scroll to the end of the document to enable the agreement.
              </p>
            )}

            <label className="flex cursor-pointer items-start gap-3 text-sm text-slate-200">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={!scrolledToBottom || submitting}
                className="mt-0.5 h-4 w-4 rounded border-white/20 bg-white/10 text-[#6558f5] focus:ring-2 focus:ring-[#6558f5]/40 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <span>
                I have read and agree to the terms above.
              </span>
            </label>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="signedName"
                  className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300"
                >
                  Type your full name
                </label>
                <input
                  id="signedName"
                  type="text"
                  value={signedName}
                  onChange={(e) => setSignedName(e.target.value)}
                  disabled={!scrolledToBottom || submitting}
                  autoComplete="name"
                  required
                  className="w-full rounded-md border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-[#6558f5] focus:outline-none focus:ring-2 focus:ring-[#6558f5]/40 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label
                  htmlFor="signedBirthDate"
                  className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-300"
                >
                  Date of birth
                </label>
                <DatePicker
                  id="signedBirthDate"
                  birthDate
                  value={signedBirthDate}
                  onChange={setSignedBirthDate}
                  disabled={!scrolledToBottom || submitting}
                  required
                  className="border-white/10 bg-white/5 focus-within:border-[#6558f5] focus-within:ring-2 focus-within:ring-[#6558f5]/40 disabled:opacity-50"
                  inputClassName="text-white placeholder:text-slate-400 disabled:text-slate-400"
                />
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleDecline}
                disabled={submitting}
                className="rounded-md border border-white/10 px-4 py-2.5 text-sm font-medium text-slate-200 transition-colors hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Decline and sign out
              </button>
              <button
                type="button"
                onClick={handleAgree}
                disabled={acceptDisabled}
                className="rounded-md bg-[#6558f5] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#766af8] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? 'Saving…' : 'I Agree'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
